/**
 * @file scan.js
 * Entry point for the /scan/ page. Requests camera access, mounts a
 * QrScanner on the live video element, and on a successful QR decode
 * redirects the visitor to the URL it contained (if any).
 */
import QrScanner from "qr-scanner";

const ABSOLUTE_URL_RE = /^https?:\/\//i;

/**
 * Decide whether a scanned payload looks like a URL we should follow.
 * Accepts absolute http(s) URLs and root-relative paths (e.g. "/v/abc/").
 * @param {string} text
 * @returns {boolean}
 */
function isFollowableUrl(text) {
  if (!text) return false;
  if (ABSOLUTE_URL_RE.test(text)) return true;
  if (text.startsWith("/")) return true;
  return false;
}

/**
 * Display an inline error message in the dedicated status node.
 * @param {string} message
 */
function showError(message) {
  const statusEl = document.getElementById("qr-status");
  if (statusEl) {
    statusEl.textContent = message;
    statusEl.classList.remove("hidden");
  }
}

document.addEventListener("DOMContentLoaded", async () => {
  const video = document.getElementById("qr-video");
  if (!video) return;

  // Check that at least one camera is available before prompting.
  const hasCamera = await QrScanner.hasCamera();
  if (!hasCamera) {
    showError(video.dataset.errorNoCamera || "Aucune caméra détectée sur cet appareil.");
    return;
  }

  let alreadyHandled = false;

  const scanner = new QrScanner(
    video,
    (result) => {
      // With returnDetailedScanResult the callback always receives { data }.
      const text = result && result.data;
      if (alreadyHandled || !text) return;

      if (!isFollowableUrl(text)) {
        // Display the payload but do not redirect — defensive against
        // malicious QR codes carrying arbitrary text or unsupported schemes.
        showError(video.dataset.errorNotUrl || "QR code détecté mais ne contient pas d'URL valide.");
        return;
      }

      alreadyHandled = true;
      scanner.stop();
      window.location.href = text;
    },
    {
      preferredCamera: "environment",
      highlightScanRegion: false,
      highlightCodeOutline: false,
      // Scan the whole frame rather than qr-scanner's default centred 2/3
      // square. The video is shown with object-cover, and iOS Safari often
      // delivers a stream whose resolution/orientation differs from Android's,
      // so the centred default region could leave the QR the visitor aligns in
      // the frame outside the decoded area — never matching on iOS.
      calculateScanRegion: (v) => ({
        x: 0,
        y: 0,
        width: v.videoWidth,
        height: v.videoHeight,
        downScaledWidth: 400,
        downScaledHeight: 400,
      }),
      // Give iOS (which decodes via the worker, lacking BarcodeDetector) more
      // decode attempts per second.
      maxScansPerSecond: 25,
      returnDetailedScanResult: true,
      // DIAGNOSTIC (temporary): surface real decoder errors. The routine
      // "No QR code found" fires on every empty frame and must be ignored.
      onDecodeError: (err) => {
        const msg = typeof err === "string" ? err : err && err.message;
        if (msg && !/no qr code found/i.test(msg)) {
          console.error("[scan] decode error:", err);
        }
      },
    },
  );

  // Decode both normal and inverted QR codes (light modules on a dark
  // background). Our generated codes are light (#F5F1E6), so when printed on a
  // dark panel they are effectively inverted; without this the WASM engine
  // (used on iOS, which lacks BarcodeDetector) never matches them.
  scanner.setInversionMode("both");

  // Fade the live feed in once the stream actually starts playing so the
  // decorative scan-frame illustration stays alone on screen until then.
  const revealVideo = () => {
    const reveal = () => video.classList.replace("opacity-0", "opacity-100");
    if (video.readyState >= 2) {
      reveal();
    } else {
      video.addEventListener("loadeddata", reveal, { once: true });
    }
  };

  const startScanner = async () => {
    try {
      await scanner.start();
      revealVideo();
    } catch (err) {
      // Most likely cause: user denied the camera permission prompt.
      console.error("[scan] Camera start failed:", err);
      showError(video.dataset.errorPermission || "Accès à la caméra refusé ou indisponible.");
    }
  };

  await startScanner();

  // Safari/iOS restores this page from the back-forward cache when the visitor
  // taps "back" from a scanned page: DOMContentLoaded does not fire again and
  // the camera stream is left dead (no permission re-prompt, frozen feed). Re-
  // acquire the camera on bfcache restore so the feed is live without a manual
  // page refresh.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    alreadyHandled = false;
    startScanner();
  });
});
